import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, catchError } from 'rxjs';
import { ListResult } from '../api/core/list-result';
import {
  FileDto,
  Letter,
  Matter,
  MatterForm,
  MatterProcess,
  WorkflowStep,
  MatterCreateUpdate,
  MatterFilter,
  MatterReceipt,
  CreateUpdateMatterReceipt,
  MatterCreateDTO,
  Task,
  ActionParameterDto,
  MatterProcessFieldsQuestionnaireFormDto,
  MatterRelation,
  LinkDto
} from '../api/data-model';
import { BACKEND_URL } from '../app.config';
import {
  BulkDeleteMatterRequestedDocumentsResult,
  RequestedDocument,
} from '../api/matter/matter-document-request';
import { DocumentDownload } from '../api/matter/matter-document-download';
import { FileWithContentDto } from "../api/document";
import { RelatedMatter } from '../api/matter/related-matter';
import { ValidationFormDto } from '../api/matter/validation-form-dto';
import { EmailLog } from '../api/matter/email-log';
import { Email } from '../api/email';
import { FormParentFieldDataDto } from '../api/matter/matter-parent-field-data';
import {
  MatterForFormCopyDto,
  MatterForFormCopyRequest,
} from '../components/admin/form/copy-form/copy-form';

@Injectable()
export class MatterService {
  constructor(private readonly http: HttpClient, @Inject(BACKEND_URL) private readonly baseUrl: string, private readonly router: Router) { }

  getList(filter: any, sorting: string = 'businessCode asc', skipCount: number = 0, maxResultCount: number = 10): Observable<ListResult<Matter>> {
    return this.http.get<ListResult<Matter>>(`${this.baseUrl}/api/app/matter`, {
      params: {
        scope: filter.scope ?? '',
        matterFilter: filter.matterFilter ?? '',
        businessCode: filter.businessCode ?? '',
        foreignNationalFirstName: filter.foreignNationalFirstName ?? '',
        foreignNationalLastName: filter.foreignNationalLastName ?? '',
        receiptNumber: filter.receiptNumber ?? '',
        matterName: filter.matterName ?? '',
        companyName: filter.companyName ?? '',
        sorting,
        skipCount: skipCount.toString(),
        maxResultCount: maxResultCount.toString(),
      },
    });
  }

  exportToCsv(filter: any, sorting: string): Observable<Blob> {
    return this.http.get(this.baseUrl + '/api/app/matter/exporting', {
      responseType: 'blob',
      params: {
        scope: filter.scope ?? '',
        matterFilter: filter.matterFilter ?? '',
        businessCode: filter.businessCode ?? '',
        foreignNationalFirstName: filter.foreignNationalFirstName ?? '',
        foreignNationalLastName: filter.foreignNationalLastName ?? '',
        receiptNumber: filter.receiptNumber ?? '',
        matterName: filter.matterName ?? '',
        companyName: filter.companyName ?? '',
        sorting,
      },
    });
  }

  exportByForm(formId: string): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/api/app/matter/exporting-by-form/${formId}`,
      { responseType: 'blob' },
    );
  }

  addMatter(matter: MatterCreateDTO): Observable<Matter> {
    return this.http.post<Matter>(this.baseUrl + '/api/app/matter', matter);
  }

  // updateMatter(matter: MatterCreateUpdate): Observable<Matter> {
  //   return this.http.put<Matter>(`${this.baseUrl}/api/app/matter/${matter.id}`, matter);
  // }

  updateMatterInformation(matter: MatterCreateUpdate): Observable<Matter> {
    return this.http.put<Matter>(`${this.baseUrl}/api/app/matter/${matter.id}/information`, matter);
  }

  updateMatterContacts(matter: MatterCreateUpdate): Observable<Matter> {
    return this.http.put<Matter>(`${this.baseUrl}/api/app/matter/${matter.id}/contacts`, matter);
  }

  updateMatterDates(matter: MatterCreateUpdate): Observable<Matter> {
    return this.http.put<Matter>(`${this.baseUrl}/api/app/matter/${matter.id}/dates`, matter);
  }

  getMatter(id: string): Observable<Matter> {
    return this.http.get<Matter>(this.baseUrl + '/api/app/matter/' + id);
  }

  ensureSharePointStructure(matterId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/api/app/matter/${matterId}/ensure-sharepoint-structure`,
      null
    );
  }

  getProcessModuleInfo(id: string): Observable<Record<string, string>> {
    return this.http.get<Record<string, string>>(this.baseUrl + '/api/app/matter/' + id + '/process-module-info');
  }

  getMatterByCode(code: string): Observable<Matter> {
    return this.http.get<Matter>(this.baseUrl + '/api/app/matter/by-code?code=' + code).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          // Navigate to 404 page with context information
          this.router.navigate(['/notfound'], {
            queryParams: {
              type: 'matter',
              code: code,
              message: `Matter with code "${code}" not found`
            }
          });
          // Return an empty Observable that never completes to prevent further processing
          return new Observable<Matter>(() => { });
        }
        return throwError(() => error);
      })
    );
  }

  getMatterIdByCode(code: string): Observable<Matter> {
    return this.http.get<Matter>(this.baseUrl + '/api/app/matter/id-by-code?code=' + code);
  }
  Export9089Report(code: string): Observable<Matter> {
    return this.http.get<Matter>(this.baseUrl + '/api/app/matter/id-by-code?code=' + code);
  }
  getStepsForDashboard(matterId: string): Observable<WorkflowStep[]> {
    return this.http.get<WorkflowStep[]>(this.baseUrl + '/api/app/matter/' + matterId + '/step');
  }

  getSteps(id: string): Observable<MatterProcess> {
    return this.http.get<MatterProcess>(this.baseUrl + '/api/app/matter/' + id + '/process');
  }

  getStep(matterId: string, stepId: string, includeActions: boolean = false): Observable<WorkflowStep> {
    return this.http.get<WorkflowStep>(this.baseUrl + '/api/app/matter/' + matterId + '/step/' + stepId, {
      params: {
        includeActions: includeActions,
      },
    });
  }

  updateStep(matterId: string, stepId: string, step: { dueDate: string; comments: string }): Observable<WorkflowStep> {
    return this.http.put<WorkflowStep>(this.baseUrl + '/api/app/matter/' + matterId + '/step/' + stepId, step);
  }

  getProcessFields(matterId: string): Observable<MatterProcessFieldsQuestionnaireFormDto> {
    return this.http.get<any>(this.baseUrl + '/api/app/matter/' + matterId + '/process-fields');
  }

  updaterProcessFields(matterId: string, formData: string): Observable<any> {
    return this.http.put<any>(this.baseUrl + '/api/app/matter/' + matterId + '/process-fields', { formData: formData });
  }

  putStep(matterId: string, stepId: string, step: WorkflowStep): Observable<WorkflowStep> {
    return this.http.put<WorkflowStep>(this.baseUrl + '/api/app/matter/' + matterId + '/step/' + stepId, step);
  }

  resetStep(matterId: string, stepId: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/api/app/matter/${matterId}/step/${stepId}/reset`, null);
  }

  invokeAction(matterId: string, actionId: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/api/app/matter/${matterId}/action/${actionId}/invoke`, null);
  }

  submitAction(matterId: string, actionId: string, parameter: ActionParameterDto): Observable<any> {
    const formData = new FormData();
    formData.append('Value', parameter.value);

    if (parameter.files && parameter.files.length > 0) {
      parameter.files.forEach(file => {
        formData.append('Files', file);
      });
    }

    return this.http.post<any>(`${this.baseUrl}/api/app/matter/${matterId}/action/${actionId}/submit`, formData);
  }

  skipAction(matterId: string, actionId: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/api/app/matter/${matterId}/action/${actionId}/skip`, null);
  }

  skipStepActions(matterId: string, stepId: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/api/app/matter/${matterId}/step/${stepId}/skip`, null);
  }

  getFilters(scope: number): Observable<MatterFilter[]> {
    return this.http.get<MatterFilter[]>(`${this.baseUrl}/api/app/matter/filters`, {
      params: {
        scope: scope || '',
      },
    });
  }

  generateLetter(letterId: string, matterId: string): Observable<LinkDto> {
    return this.http.post(
      this.baseUrl + `/api/app/matter/${matterId}/letter/${letterId}`,
      {}
    );
  }

  getLetters(matterId: string): Observable<Letter[]> {
    return this.http.get<Letter[]>(this.baseUrl + `/api/app/matter/${matterId}/letters`);
  }

  getLetterList(): Observable<ListResult<Letter>> {
    return this.http.get<ListResult<Letter>>(this.baseUrl + '/api/app/matter/letters');
  }

  addMatterLetter(matterId: string, letterId: string): Observable<Letter> {
    return this.http.post<Letter>(`${this.baseUrl}/api/app/matter/${matterId}/matter-letter/${letterId}`, {});
  }


  getLetterFile(letterId: string, matterId: string): Observable<FileDto> {
    return this.http.get<FileDto>(this.baseUrl + `/api/app/matter/${matterId}/letter-template/${letterId}`);
  }

  generateMatterForm(matterId: string, formId: string, applicantId?: string): Observable<LinkDto> {
    return this.http.post(
      this.baseUrl + `/api/app/matter/${matterId}/form/${formId}`,
      { applicantId: applicantId }
    );
  }

  validationForm(matterId: string, formNos: string, isEfileTest?: boolean): Observable<ValidationFormDto[]> {
    const url = this.baseUrl + `/api/app/matter/${matterId}/e-file/${formNos}/validation` + (isEfileTest ? '?isEfileTest=true' : '');
    return this.http.post<ValidationFormDto[]>(url, {});
  }

  bulkGenerateMatterForm(matterId: string, forms?: any): Observable<any> {
    return this.http.post(
      this.baseUrl + `/api/app/matter/${matterId}/bulk-create-forms`,
      { forms: forms }
    );
  }

  getMatterForms(matterId: string): Observable<MatterForm[]> {
    return this.http.get<MatterForm[]>(this.baseUrl + `/api/app/matter/${matterId}/forms`);
  }
  addMatterForm(id: string, matterForm: MatterForm): Observable<MatterForm> {
    return this.http.post<MatterForm>(`${this.baseUrl}/api/app/matter/matter-form/${id}`, matterForm);
  }

  deleteMatterForm(matterId: string, formId: string, applicantId: string): Observable<void> {
    return this.http.delete<void>( 
      this.baseUrl + `/api/app/matter/matter-form/${matterId}`,
      {
        params: {
          formId: formId,
          applicantId: applicantId
        }
      }
    );
  }

  getMatterFormFile(formId: string, matterId: string): Observable<FileDto> {
    return this.http.get<FileDto>(this.baseUrl + `/api/app/matter/${matterId}/form-template/${formId}`);
  }

  getFormParentFieldData(matterId: string, formId: string, applicantId?: string): Observable<FormParentFieldDataDto> {
    // form virtual field data is stored per applicant, so pass the applicant of the child form
    const query = applicantId ? `?applicantId=${encodeURIComponent(applicantId)}` : '';
    return this.http.get<FormParentFieldDataDto>(
      `${this.baseUrl}/api/app/matter/${matterId}/form/${formId}/parent-field-data${query}`
    );
  }

  updatePosition(matterId: string, data: { companyAssignType: number; id: string | null }): Observable<any> {
    return this.http.put<any>(this.baseUrl + `/api/app/matter/${matterId}/position`, data);
  }

  exportKeywordHelper(matterId: string, formNo: string): Observable<Blob> {
    return this.http.get(this.baseUrl + `/api/app/matter/r-pTKeywords`, {
      responseType: 'blob',
      params: {
        formNo: formNo,
        matterId: matterId
      },
    });
  }
  unassignPW(matterId: string): Observable<any> {
    return this.http.delete<any>(this.baseUrl + `/api/app/matter/${matterId}/prevailing-wage`);
  }

  getReceipts(id: string, sorting: string, skipCount: number, maxResultCount: number): Observable<ListResult<MatterReceipt>> {
    return this.http.get<ListResult<MatterReceipt>>(`${this.baseUrl}/api/app/matter/${id}/receipts`, {
      params: {
        sorting,
        skipCount,
        maxResultCount,
      },
    });
  }

  getReceipt(id: string, forcePrimary: boolean, receiptId?: string): Observable<CreateUpdateMatterReceipt> {
    return this.http.get<CreateUpdateMatterReceipt>(`${this.baseUrl}/api/app/matter/${id}/receipt`, {
      params: {
        receiptId: receiptId ?? '',
        forcePrimary: forcePrimary.toString(),
      },
    });
  }

  addReceipt(id: string, receipt: CreateUpdateMatterReceipt): Observable<MatterReceipt> {
    return this.http.post<MatterReceipt>(`${this.baseUrl}/api/app/matter/${id}/receipt`, receipt);
  }

  updateReceipt(id: string, receiptId: string, receipt: CreateUpdateMatterReceipt): Observable<MatterReceipt> {
    return this.http.put<MatterReceipt>(`${this.baseUrl}/api/app/matter/${id}/receipt/${receiptId}`, receipt);
  }

  updateReceiptPrimary(id: string, receiptId: string): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/api/app/matter/${id}/receipt-primary/${receiptId}`, null);
  }

  deleteReceipt(id: string, receiptId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/api/app/matter/${id}/receipt/${receiptId}`);
  }

  generateLetterByName(matterId: string, letterName: string): Observable<LinkDto> {
    return this.http.post(`${this.baseUrl}/api/app/matter/${matterId}/letter`, null, {
      params: {
        name: letterName,
      }
    });
  }

  getTask(matterId: string, taskId: string): Observable<Task> {
    return this.http.get<Task>(`${this.baseUrl}/api/app/matter/${matterId}/task/${taskId}`);
  }

  getTasks(id: string): Observable<Task[]> {
    return this.http.get<Task[]>(`${this.baseUrl}/api/app/matter/${id}/tasks`);
  }

  addTask(id: string, task: Task): Observable<Task> {
    return this.http.post<Task>(`${this.baseUrl}/api/app/matter/${id}/task`, task);
  }

  updateTask(id: string, taskId: string, task: Task): Observable<Task> {
    return this.http.put<Task>(`${this.baseUrl}/api/app/matter/${id}/task/${taskId}`, task);
  }

  getRequestedDocuments(matterId: string): Observable<RequestedDocument[]> {
    return this.http.get<RequestedDocument[]>(this.baseUrl + `/api/app/matter/${matterId}/requested-documents`);
  }

  addMatterRequestedDoc(matterId: string, doc: RequestedDocument): Observable<RequestedDocument> {
    return this.http.post<RequestedDocument>(this.baseUrl + `/api/app/matter/${matterId}/requested-document`, doc);
  }
  updateMatterRequestedDoc(id: string, doc: RequestedDocument): Observable<RequestedDocument> {
    return this.http.put<RequestedDocument>(this.baseUrl + `/api/app/matter/${id}/requested-document`, doc);
  }
  addRequestedDocDocuments(matterId: string, id: string, questionnaireId?: string, applicantId?: string, docs?: string[], documentType?: string): Observable<RequestedDocument> {
    return this.http.post<RequestedDocument>(this.baseUrl + `/api/app/matter/${matterId}/requested-doc-documents/${id}`, {
      questionnaireId: questionnaireId,
      applicantId: applicantId,
      documents: docs,
      documentType: documentType
    });
  }

  deleteRequestedDocument(id: string, requestedDocId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/api/app/matter/${id}/requested-document/${requestedDocId}`);
  }
  
  bulkDeleteRequestedDocuments(
    matterId: string,
    requestedDocumentIds: string[]
  ): Observable<BulkDeleteMatterRequestedDocumentsResult> {
    return this.http.post<BulkDeleteMatterRequestedDocumentsResult>(
      `${this.baseUrl}/api/app/matter/${matterId}/requested-documents/delete`,
      { requestedDocumentIds }
    );
  }

  bulkDeleteDocumentsDownloads(matterId: string, docIds: string[]): Observable<BulkDeleteMatterRequestedDocumentsResult> {
    return this.http.post<BulkDeleteMatterRequestedDocumentsResult>(
      `${this.baseUrl}/api/app/matter/${matterId}/document-download/delete`,
      { docId: docIds }
    );
  }

  deleteHybridRequestedDoDocument(id: string, questionnaireId: string, applicantId: string, docRequestId: string, documentId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/api/app/matter/${id}/hybrid-requested-doc-document`, { params: { questionnaireId, applicantId, docRequestId, documentId } });
  }

  deleteInternalRequestedDoDocument(id: string, documentId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/api/app/matter/${id}/internal-requested-doc-document/${documentId}`);
  }

  createDocumentRequestDownload(matterId: string, requestDownloadInfo: any): Observable<RequestedDocument[]> {
    return this.http.post<RequestedDocument[]>(this.baseUrl + `/api/app/matter/${matterId}/document-request-download`, requestDownloadInfo);
  }

  getDocumentDownloads(matterId: string): Observable<RequestedDocument[]> {
    return this.http.get<RequestedDocument[]>(this.baseUrl + `/api/app/matter/${matterId}/document-downloads`);
  }

  getDocumentDownloadContent(matterId: string, docId: string) {
    return this.http.get<FileWithContentDto>(`${this.baseUrl}/api/app/matter/${matterId}/document-download/${docId}`);
  }

  addDocumentDownload(matterId: string, doc: DocumentDownload): Observable<DocumentDownload> {
    return this.http.post<DocumentDownload>(this.baseUrl + `/api/app/matter/${matterId}/document-download`, doc);
  }

  updateDocumentDownload(id: string, doc: DocumentDownload): Observable<DocumentDownload> {
    return this.http.put<DocumentDownload>(this.baseUrl + `/api/app/matter/${id}/document-download`, doc);
  }

  deleteDocumentDownload(id: string, requestedDocId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/api/app/matter/${id}/document-download/${requestedDocId}`);
  }

  getMatterRelations(id: string): Observable<MatterRelation[]> {
    return this.http.get<MatterRelation[]>(`${this.baseUrl}/api/app/matter/${id}/matter-relations`);
  }

  getRelatedMatters(id: string): Observable<RelatedMatter[]> {
    return this.http.get<RelatedMatter[]>(`${this.baseUrl}/api/app/matter/${id}/matters-for-association`);
  }

  getMattersForFormCopy(
    formId: string,
    input: MatterForFormCopyRequest,
  ): Observable<ListResult<MatterForFormCopyDto>> {
    const params: Record<string, string> = {
      SkipCount: String(input.skipCount ?? 0),
      MaxResultCount: String(input.maxResultCount ?? 1000),
      Sorting: input.sorting ?? 'matterName',
    };

    if (input.companyName) {
      params['CompanyName'] = input.companyName;
    }
    if (input.caseManagerId) {
      params['CaseManagerId'] = input.caseManagerId;
    }
    if (input.primaryCaseManagerId) {
      params['PrimaryCaseManagerId'] = input.primaryCaseManagerId;
    }
    if (input.signingAttorneyId) {
      params['SigningAttorneyId'] = input.signingAttorneyId;
    }
    if (input.primarySigningAttorneyId) {
      params['PrimarySigningAttorneyId'] = input.primarySigningAttorneyId;
    }
    if (input.progressStatusId) {
      params['ProgressStatusId'] = input.progressStatusId;
    }

    return this.http.get<ListResult<MatterForFormCopyDto>>(
      `${this.baseUrl}/api/app/matter/matters/${formId}`,
      { params },
    );
  }

  addMatterRelation(id: string, relation: { matterId: string, sourceId: string }): Observable<MatterRelation> {
    return this.http.post<MatterRelation>(this.baseUrl + `/api/app/matter/${id}/matter-relation`, relation);
  }

  deleteMatterRelation(id: string, sourceId: string) {
    return this.http.delete<any>(`${this.baseUrl}/api/app/matter/${id}/matter-relation/${sourceId}`);
  }

  getEmailLogs(id: string): Observable<EmailLog[]> {
    return this.http.get<EmailLog[]>(`${this.baseUrl}/api/app/matter/${id}/email-log-by-matter-id`);
  }

  prepareEmailFromTemplate(matterId: string, emailTemplateId: string): Observable<{ type: number; data: string }> {
    return this.http.post<{ type: number; data: string }>(
      `${this.baseUrl}/api/app/matter/${matterId}/email-template/${emailTemplateId}/prepare`, null
    );
  }

  sendEmail(matterId: string, email: Email, files: File[]): Observable<any> {
    const formData = new FormData();
    formData.append('Value', JSON.stringify(email));

    if (files && files.length > 0) {
      files.forEach(file => {
        formData.append('Files', file);
      });
    }

    return this.http.post(
      `${this.baseUrl}/api/app/matter/${matterId}/sendmail`,
      formData
    );
  }
}
